import axios from 'axios';

import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// Redux.
import { connect } from 'react-redux';
import {
    setPodcastSubscribeStatus,
    setSpeakerSubscribeStatus,
    setTopicPreferenceStatus,
    syncSubscribeStatuses,
    syncTopicPreferences,
} from '../redux/actions/userActions';

// Router.
import { Link, Navigate, useNavigate } from 'react-router-dom';

// Firebase.
import { getAnalytics, logEvent } from "firebase/analytics";

import { ChevronRight, Refresh } from '@mui/icons-material';
import { shuffle } from 'lodash';

import { validTokenExists } from '../firebaseHelper';
import BottomMenu from '../components/BottomMenu';
import Loading from '../components/Loading';
import {
    TopicPreferenceRow,
    SpeakerPreferenceRow,
    ShowPreferenceRow,
    analyticsEventName,
} from '../components/SettingsPreferenceComponents';
import { getSubscribeStatus, normalizeSubscribeStatus } from '../utils/subscribeStatus';
import { PREFERENCE_COPY_TITLE } from '../constants/labels';

const OVERVIEW_EMPTY = {
    topicIds: [],
    topicStatuses: [],
    speakers: [],
    speakerSubscribeStatuses: [],
    sources: [],
    podcastSubscribeStatuses: [],
};

const OVERVIEW_LOAD_ERROR_MESSAGE = 'Unable to load For me. Please try again later.';
const OVERVIEW_PREVIEW_LIMIT = 6;

const NOOP = () => {};

const postTopicPreferenceStatus = (topicId, topicStatus) => (
    validTokenExists()
        .then(() => axios.post('/set_topic_status', {
            topicId,
            topicStatus,
            deferFeedRefresh: true,
        }))
);

const OverviewSection = ({
    title,
    footerLabel,
    browseTo,
    showFooterLink = false,
    children,
}) => (
    <section className='settingsOverviewSection'>
        <h2 className='settingsOverviewSectionTitle'>
            <Link className='settingsOverviewSectionLink' to={browseTo} aria-label={`View all ${title.toLowerCase()}`}>
                <span>{title}</span>
                <ChevronRight className='settingsOverviewSectionChevron' aria-hidden='true' />
            </Link>
        </h2>
        {children}
        {showFooterLink && footerLabel && browseTo && (
            <div className='settingsOverviewFooter'>
                <Link className='settingsOverviewFooterLink' to={browseTo}>
                    <span>{footerLabel}</span>
                    <ChevronRight className='settingsOverviewFooterChevron' aria-hidden='true' />
                </Link>
            </div>
        )}
    </section>
);

const TrendingPodcasts = ({
    loading,
    sources,
    podcastSubscribeIds,
    podcastSubscribeStatusById,
    refresh,
    setPodcastSubscribeStatus,
}) => {
    const navigate = useNavigate();

    if (loading) {
        return (
            <div className='settingsPreferenceRows glow'>
                <div className='loadingSettingForMeTopic' />
                <div className='loadingSettingForMeTopic' />
                <div className='loadingSettingForMeTopic' />
            </div>
        );
    }

    if (sources.length === 0) {
        return null;
    }

    return (
        <div className='settingsTrendingBlock'>
            <div className='settingsTrendingHeader'>
                <span>Trending shows</span>
                <button type='button' className='settingsTrendingRefreshButton' onClick={refresh} aria-label='Refresh trending shows'>
                    <Refresh className='settingsTrendingRefreshIcon' />
                </button>
            </div>
            <div className='settingsPreferenceRows'>
                {sources.map((source) => {
                    const currentStatus = getSubscribeStatus(
                        podcastSubscribeStatusById,
                        podcastSubscribeIds,
                        source.id,
                    );
                    return (
                        <ShowPreferenceRow
                            key={source.id}
                            source={source}
                            status={currentStatus}
                            onOpen={() => navigate(`/podcast/${source.id}`)}
                            onStatusChange={(nextStatus) => setPodcastSubscribeStatus(source.id, nextStatus)}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export const Settings = ({
    authenticated = null,
    uid = '',
    setTopicPreferenceStatus = postTopicPreferenceStatus,
    syncTopicPreferences = NOOP,
    syncSubscribeStatuses,
    setSpeakerSubscribeStatus,
    setPodcastSubscribeStatus,
    speakerSubscribeIds = [],
    speakerSubscribeStatusById = {},
    podcastSubscribeIds = [],
    podcastSubscribeStatusById = {},
}) => {
    const navigate = useNavigate();

    const [overview, setOverview] = useState(null);
    const [loadingOverview, setLoadingOverview] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [overviewTopicStatusById, setOverviewTopicStatusById] = useState({});
    const [overviewTopicIdsWithNilStatus, setOverviewTopicIdsWithNilStatus] = useState(new Set());
    const [trendingSpeakers, setTrendingSpeakers] = useState([]);
    const [trendingPodcasts, setTrendingPodcasts] = useState([]);
    const [loadingTrendingSpeakers, setLoadingTrendingSpeakers] = useState(false);
    const [loadingTrendingPodcasts, setLoadingTrendingPodcasts] = useState(false);

    const visibleTopicIds = (overview?.topicIds || []).slice(0, OVERVIEW_PREVIEW_LIMIT);
    const visibleSpeakers = (overview?.speakers || []).slice(0, OVERVIEW_PREVIEW_LIMIT);
    const visibleSources = (overview?.sources || []).slice(0, OVERVIEW_PREVIEW_LIMIT);

    const loadOverview = useCallback(() => {
        setLoadingOverview(true);
        setErrorMessage(null);

        validTokenExists()
            .then(() => axios.get('/for_me_overview'))
            .then((res) => {
                const nextOverview = res.data || OVERVIEW_EMPTY;
                const nextTopicStatusById = {};
                (nextOverview.topicStatuses || []).forEach((topicStatus) => {
                    const normalizedStatus = normalizeSubscribeStatus(topicStatus.topicStatus);
                    if (normalizedStatus !== null) {
                        nextTopicStatusById[topicStatus.topicId] = normalizedStatus;
                    }
                });

                setOverview(nextOverview);
                setOverviewTopicStatusById(nextTopicStatusById);
                setOverviewTopicIdsWithNilStatus(new Set());
                syncTopicPreferences(nextOverview.topicStatuses || [], {
                    replace: false,
                    uid,
                });
                syncSubscribeStatuses({
                    speakerSubscribeStatuses: nextOverview.speakerSubscribeStatuses || [],
                    podcastSubscribeStatuses: nextOverview.podcastSubscribeStatuses || [],
                });
                setLoadingOverview(false);
            })
            .catch((err) => {
                console.log(err);
                setOverview(OVERVIEW_EMPTY);
                setErrorMessage(OVERVIEW_LOAD_ERROR_MESSAGE);
                setLoadingOverview(false);
            });
    }, [syncSubscribeStatuses, syncTopicPreferences, uid]);

    const loadTrendingSpeakers = useCallback(() => {
        setLoadingTrendingSpeakers(true);
        axios
            .get('/top_speakers')
            .then((res) => {
                setTrendingSpeakers(shuffle(res.data?.speakers || []));
                setLoadingTrendingSpeakers(false);
            })
            .catch((err) => {
                console.log(err);
                setTrendingSpeakers([]);
                setLoadingTrendingSpeakers(false);
            });
    }, []);

    const loadTrendingPodcasts = useCallback(() => {
        setLoadingTrendingPodcasts(true);
        axios
            .get('/top_podcasts')
            .then((res) => {
                setTrendingPodcasts(shuffle(res.data?.sources || []));
                setLoadingTrendingPodcasts(false);
            })
            .catch((err) => {
                console.log(err);
                setTrendingPodcasts([]);
                setLoadingTrendingPodcasts(false);
            });
    }, []);

    useEffect(() => {
        const analytics = getAnalytics();
        logEvent(analytics, 'screen_view', {
            firebase_screen: 'settings'
        });
    }, []);

    useEffect(() => {
        if (authenticated === true) {
            loadOverview();
            loadTrendingSpeakers();
            loadTrendingPodcasts();
        }
    }, [authenticated, loadOverview, loadTrendingSpeakers, loadTrendingPodcasts]);

    const overviewTopicStatus = (topicId) => {
        if (overviewTopicIdsWithNilStatus.has(topicId)) {
            return null;
        }
        if (Object.prototype.hasOwnProperty.call(overviewTopicStatusById, topicId)) {
            return overviewTopicStatusById[topicId];
        }
        return true;
    };

    const setOverviewTopicStatus = (topicId, topicStatus) => {
        const normalizedTopicStatus = normalizeSubscribeStatus(topicStatus);
        const previousHadStatus = Object.prototype.hasOwnProperty.call(overviewTopicStatusById, topicId);
        const previousStatus = overviewTopicStatusById[topicId];
        const previousHadNilStatus = overviewTopicIdsWithNilStatus.has(topicId);

        setErrorMessage(null);
        setOverviewTopicStatusById((currentStatusById) => {
            const nextStatusById = { ...currentStatusById };
            if (normalizedTopicStatus === null) {
                delete nextStatusById[topicId];
            } else {
                nextStatusById[topicId] = normalizedTopicStatus;
            }
            return nextStatusById;
        });
        setOverviewTopicIdsWithNilStatus((currentTopicIdsWithNilStatus) => {
            const nextTopicIdsWithNilStatus = new Set(currentTopicIdsWithNilStatus);
            if (normalizedTopicStatus === null) {
                nextTopicIdsWithNilStatus.add(topicId);
            } else {
                nextTopicIdsWithNilStatus.delete(topicId);
            }
            return nextTopicIdsWithNilStatus;
        });

        return setTopicPreferenceStatus(topicId, normalizedTopicStatus)
            .catch((err) => {
                setOverviewTopicStatusById((current) => {
                    const next = { ...current };
                    if (previousHadStatus) next[topicId] = previousStatus;
                    else delete next[topicId];
                    return next;
                });
                setOverviewTopicIdsWithNilStatus((current) => {
                    const next = new Set(current);
                    if (previousHadNilStatus) next.add(topicId);
                    else next.delete(topicId);
                    return next;
                });
                setErrorMessage(err?.response?.data?.error || err?.response?.data?.general || 'Unable to update topic.');
            });
    };

    const filteredTrendingSpeakers = useMemo(() => (
        trendingSpeakers
            .filter((speaker) => getSubscribeStatus(speakerSubscribeStatusById, speakerSubscribeIds, speaker.id) !== true)
            .slice(0, OVERVIEW_PREVIEW_LIMIT)
    ), [speakerSubscribeIds, speakerSubscribeStatusById, trendingSpeakers]);

    const filteredTrendingPodcasts = useMemo(() => (
        trendingPodcasts
            .filter((source) => getSubscribeStatus(podcastSubscribeStatusById, podcastSubscribeIds, source.id) !== true)
            .slice(0, OVERVIEW_PREVIEW_LIMIT)
    ), [podcastSubscribeIds, podcastSubscribeStatusById, trendingPodcasts]);

    if (authenticated === false) {
        return <Navigate to={'/signin?redirect=%2Fsettings'}></Navigate>;
    }

    if (authenticated !== true) {
        return (
            <div className='container bottomPadding settingsOverviewContainer'>
                <Loading />
            </div>
        );
    }

    return (
        <div className='container settingsOverviewContainer hasBottomMenu'>
            <div className='settingsOverviewContent'>
                <div className='settingsOverviewHeader'>
                    <h1 className='settingsOverviewTitle'>{PREFERENCE_COPY_TITLE}</h1>
                </div>

                {loadingOverview && overview === null ? (
                    <div className='settingsOverviewSkeleton glow'>
                        <div className='loadingSettingForMeTopic' />
                        <div className='loadingSettingForMeTopic' />
                        <div className='loadingSettingForMeTopic' />
                        <div className='loadingSettingForMeTopic' />
                    </div>
                ) : (
                    <>
                        <OverviewSection
                            title='Topics'
                            footerLabel='All topics'
                            browseTo='/settings/topics'
                            showFooterLink={visibleTopicIds.length > 0}
                        >
                            {visibleTopicIds.length === 0 ? (
                                <div className='settingsEmptyTopics'>
                                    <div className='settingsEmptyText'>Choose topics to personalize your highlights.</div>
                                    <button
                                        type='button'
                                        className='primaryPillButton settingsGetStartedButton'
                                        onClick={() => navigate('/settings/topics')}
                                    >
                                        <span className='primaryPillButtonText'>Choose topics</span>
                                    </button>
                                </div>
                            ) : (
                                <div className='settingsPreferenceRows'>
                                    {visibleTopicIds.map((topicId) => (
                                        <TopicPreferenceRow
                                            key={topicId}
                                            topicId={topicId}
                                            topicStatus={overviewTopicStatus(topicId)}
                                            setTopicStatus={setOverviewTopicStatus}
                                            onOpen={(selectedTopicId) => navigate(`/search/${encodeURIComponent(selectedTopicId)}`)}
                                        />
                                    ))}
                                </div>
                            )}
                        </OverviewSection>

                        <OverviewSection
                            title='Speakers'
                            footerLabel='All speakers'
                            browseTo='/settings/speakers'
                            showFooterLink={visibleSpeakers.length > 0}
                        >
                            {visibleSpeakers.length === 0 ? (
                                <div className='settingsTrendingBlock'>
                                    <div className='settingsTrendingHeader'>
                                        <span>Trending speakers</span>
                                        <button type='button' className='settingsTrendingRefreshButton' onClick={loadTrendingSpeakers} aria-label='Refresh trending speakers'>
                                            <Refresh className='settingsTrendingRefreshIcon' />
                                        </button>
                                    </div>
                                    {loadingTrendingSpeakers ? (
                                        <div className='settingsPreferenceRows glow'>
                                            <div className='loadingSettingForMeTopic' />
                                            <div className='loadingSettingForMeTopic' />
                                            <div className='loadingSettingForMeTopic' />
                                        </div>
                                    ) : (
                                        <div className='settingsPreferenceRows'>
                                            {filteredTrendingSpeakers.map((speaker) => (
                                                <SpeakerPreferenceRow
                                                    key={speaker.id}
                                                    speaker={speaker}
                                                    status={getSubscribeStatus(speakerSubscribeStatusById, speakerSubscribeIds, speaker.id)}
                                                    onOpen={() => navigate(`/speaker/${speaker.id}`)}
                                                    onStatusChange={(nextStatus) => {
                                                        logEvent(getAnalytics(), analyticsEventName(nextStatus));
                                                        setSpeakerSubscribeStatus(speaker.id, nextStatus);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className='settingsPreferenceRows'>
                                    {visibleSpeakers.map((speaker) => {
                                        const currentStatus = getSubscribeStatus(
                                            speakerSubscribeStatusById,
                                            speakerSubscribeIds,
                                            speaker.id,
                                        );
                                        return (
                                            <SpeakerPreferenceRow
                                                key={speaker.id}
                                                speaker={speaker}
                                                status={currentStatus}
                                                onOpen={() => navigate(`/speaker/${speaker.id}`)}
                                                onStatusChange={(nextStatus) => {
                                                    logEvent(getAnalytics(), analyticsEventName(nextStatus));
                                                    setSpeakerSubscribeStatus(speaker.id, nextStatus);
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </OverviewSection>

                        <OverviewSection
                            title='Shows'
                            footerLabel='All shows'
                            browseTo='/settings/shows'
                            showFooterLink={visibleSources.length > 0}
                        >
                            {visibleSources.length === 0 ? (
                                <TrendingPodcasts
                                    loading={loadingTrendingPodcasts}
                                    sources={filteredTrendingPodcasts}
                                    podcastSubscribeIds={podcastSubscribeIds}
                                    podcastSubscribeStatusById={podcastSubscribeStatusById}
                                    refresh={loadTrendingPodcasts}
                                    setPodcastSubscribeStatus={setPodcastSubscribeStatus}
                                />
                            ) : (
                                <div className='settingsPreferenceRows'>
                                    {visibleSources.map((source) => {
                                        const currentStatus = getSubscribeStatus(
                                            podcastSubscribeStatusById,
                                            podcastSubscribeIds,
                                            source.id,
                                        );
                                        return (
                                            <ShowPreferenceRow
                                                key={source.id}
                                                source={source}
                                                status={currentStatus}
                                                onOpen={() => navigate(`/podcast/${source.id}`)}
                                                onStatusChange={(nextStatus) => {
                                                    logEvent(getAnalytics(), analyticsEventName(nextStatus));
                                                    setPodcastSubscribeStatus(source.id, nextStatus);
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </OverviewSection>
                    </>
                )}
            </div>

            {errorMessage && (
                <div className='settingsErrorOverlay' role='alert'>
                    <span>{errorMessage}</span>
                    <button type='button' onClick={() => setErrorMessage(null)}>Dismiss</button>
                </div>
            )}
            <BottomMenu />
        </div>
    );
};

Settings.propTypes = {
    authenticated: PropTypes.bool,
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    uid: state.user.uid,
    speakerSubscribeIds: state.user.speakerSubscribeIds,
    speakerSubscribeStatusById: state.user.speakerSubscribeStatusById,
    podcastSubscribeIds: state.user.podcastSubscribeIds,
    podcastSubscribeStatusById: state.user.podcastSubscribeStatusById,
});

const mapActionsToProps = {
    syncSubscribeStatuses,
    syncTopicPreferences,
    setSpeakerSubscribeStatus,
    setPodcastSubscribeStatus,
    setTopicPreferenceStatus,
};

export default connect(mapStateToProps, mapActionsToProps)(Settings);
